import {
  COOGO_EXECUTION_EVENT_V2_VERSION,
  type CoogoExecutionEvent,
  type CoogoExecutionEventV2,
  type CoogoHumanGateAdmission,
  type CoogoOrchestrationDependency,
  type CoogoOrchestrationNode,
  type CoogoOrchestrationNodeKind,
  type CoogoOrchestrationNodeV2,
  type CoogoOrchestrationPlan,
  type CoogoReadableOrchestrationPlan,
  normalizedDependencies,
  validateCoogoExecutionEvent,
  validateCoogoOrchestrationPlan,
} from "./contracts";

export const COOGO_EXECUTION_SNAPSHOT_VERSION =
  "coogo.execution-snapshot.v1" as const;
export const COOGO_EXECUTION_CHECKPOINT_V1_VERSION =
  "coogo.execution-checkpoint.v1" as const;
export const COOGO_MAX_LOOP_ITERATIONS = 100;

export type CoogoNodeExecutionStatus =
  | "pending"
  | "running"
  | "completed"
  | "failed"
  | "skipped"
  | "cancelled"
  | "suspended";

export type CoogoPlanExecutionStatus =
  | "running"
  | "suspended"
  | "completed"
  | "failed"
  | "cancelled";

export interface CoogoHandlerTelemetry {
  cost?: number;
  inputTokens?: number;
  outputTokens?: number;
  totalTokens?: number;
  [key: string]: unknown;
}

export interface CoogoNodeTelemetry extends CoogoHandlerTelemetry {
  attempts: number;
  durationMs: number;
}

export interface CoogoSuspension {
  reason: string;
  resumeToken?: string;
  data?: Record<string, unknown>;
}

export interface CoogoNodeHandlerResult {
  output?: unknown;
  telemetry?: CoogoHandlerTelemetry;
  suspend?: CoogoSuspension;
}

export interface CoogoNodeExecutionRecord {
  nodeId: string;
  status: CoogoNodeExecutionStatus;
  output?: unknown;
  error?: CoogoSerializedExecutionError;
  suspension?: CoogoSuspension;
  telemetry?: CoogoNodeTelemetry;
}

export interface CoogoSerializedExecutionError {
  name: string;
  message: string;
  code?: string;
  retryable?: boolean;
}

export interface CoogoExecutionSnapshot {
  schemaVersion: typeof COOGO_EXECUTION_SNAPSHOT_VERSION;
  planId: string;
  runId: string;
  projectId: string;
  planFingerprint: string;
  outputNodeIds: string[] | null;
  admissionBinding?: CoogoCheckpointAdmissionBindingV1;
  status: CoogoPlanExecutionStatus;
  sequence: number;
  nodes: Record<string, CoogoNodeExecutionRecord>;
  events: Array<CoogoExecutionEvent | CoogoExecutionEventV2>;
  updatedAt: string;
}

export type CoogoCheckpointJsonPrimitive = string | number | boolean | null;

export type CoogoCheckpointObservationV1 =
  | { path: string; presence: "absent" }
  | { path: string; presence: "present"; valueKind: "undefined" }
  | {
      path: string;
      presence: "present";
      valueKind: "json-primitive";
      value: CoogoCheckpointJsonPrimitive;
    };

export interface CoogoCheckpointExactImpactOutputV1 {
  kind: "exact-impact";
  mode: "capture" | "assert";
  observations: CoogoCheckpointObservationV1[];
  verified: boolean;
  fingerprint: string;
  capturedAt: string;
}

export interface CoogoCheckpointCompensationOutputV1 {
  kind: "compensation-result";
  disposition: "already-restored" | "owned-undo";
  verified: boolean;
}

export type CoogoCheckpointSafeOutputV1 =
  | CoogoCheckpointExactImpactOutputV1
  | CoogoCheckpointCompensationOutputV1;

export interface CoogoCheckpointGateSuspensionV1 {
  kind: "human-gate";
  reason: string;
  gateNodeId: string;
  confirmationId: string;
  expiresAt: string;
  target: {
    kind: "studio-page";
    projectId: string;
    pageId: string;
  };
}

export interface CoogoCheckpointAdmissionBindingV1 {
  gateNodeId: string;
  confirmationId: string;
  expiresAt: string;
  principalBinding: "authenticated-owner";
  sessionBinding: "verified-jwt-session";
  target: {
    kind: "studio-page";
    projectId: string;
    pageId: string;
  };
  duplicatePolicy: "reject";
  durability: "durable";
}

export interface CoogoExecutionCheckpointNodeV1 {
  nodeId: string;
  status: CoogoNodeExecutionStatus;
  error?: CoogoSerializedExecutionError;
  suspension?: CoogoCheckpointGateSuspensionV1;
  telemetry?: {
    attempts: number;
    durationMs: number;
    cost?: number;
    inputTokens?: number;
    outputTokens?: number;
    totalTokens?: number;
  };
  checkpointOutput?: CoogoCheckpointSafeOutputV1;
}

export interface CoogoExecutionCheckpointV1 {
  schemaVersion: typeof COOGO_EXECUTION_CHECKPOINT_V1_VERSION;
  planId: string;
  runId: string;
  projectId: string;
  planFingerprint: string;
  outputNodeIds: string[] | null;
  admissionBinding?: CoogoCheckpointAdmissionBindingV1;
  status: CoogoPlanExecutionStatus;
  sequence: number;
  nodes: Record<string, CoogoExecutionCheckpointNodeV1>;
  updatedAt: string;
}

export interface CoogoLoadedExecution {
  checkpointRevision: number;
  snapshot: CoogoExecutionSnapshot;
}

export interface CoogoExecutionTransition {
  expectedCheckpointRevision: number;
  event: CoogoExecutionEventV2;
  nextCheckpoint: CoogoExecutionCheckpointV1;
  lease?: { leaseId: string; fencingToken: number };
}

export interface CoogoExecutionTransitionReceipt {
  checkpointRevision: number;
  eventId: string;
  sequence: number;
  replayed: boolean;
}

export interface CoogoExecutionJournal {
  load(runId: string): Promise<CoogoLoadedExecution | undefined>;
  commitTransition(
    transition: CoogoExecutionTransition,
  ): Promise<CoogoExecutionTransitionReceipt>;
}

export interface CoogoExecutionLease {
  leaseId: string;
  fencingToken: number;
  expiresAt: string;
}

export interface CoogoExecutionLeaseController {
  acquire(input: {
    plan: CoogoReadableOrchestrationPlan;
    snapshot: CoogoExecutionSnapshot;
    resumeData: Readonly<Record<string, unknown>>;
    signal: AbortSignal;
  }): Promise<CoogoExecutionLease>;
  renew(
    lease: CoogoExecutionLease,
    signal: AbortSignal,
  ): Promise<CoogoExecutionLease>;
  assertCurrent(lease: CoogoExecutionLease, signal: AbortSignal): Promise<void>;
  release(
    lease: CoogoExecutionLease,
    disposition: "suspended" | "terminal",
  ): Promise<void>;
}

export interface LegacyCoogoExecutionSnapshotJournal {
  load(runId: string): Promise<CoogoExecutionSnapshot | undefined>;
  save(snapshot: CoogoExecutionSnapshot): Promise<void>;
}

const CHECKPOINT_PLAN_STATUSES = new Set<CoogoPlanExecutionStatus>([
  "running",
  "suspended",
  "completed",
  "failed",
  "cancelled",
]);
const CHECKPOINT_NODE_STATUSES = new Set<CoogoNodeExecutionStatus>([
  "pending",
  "running",
  "completed",
  "failed",
  "skipped",
  "cancelled",
  "suspended",
]);
const EXACT_IMPACT_NODE_IDS = new Set([
  "n1-impact-before",
  "n5-impact-after",
  "r2-verify-n4-restore",
  "r2-verify-n5-restore",
]);
const COMPENSATION_NODE_IDS = new Set([
  "r1-undo-n4-failure",
  "r1-undo-n5-failure",
]);

export function parseCoogoExecutionCheckpointV1(
  value: unknown,
): CoogoExecutionCheckpointV1 {
  const checkpoint = checkpointRecord(value, "checkpoint");
  checkpointOnlyKeys(checkpoint, "checkpoint", [
    "schemaVersion",
    "planId",
    "runId",
    "projectId",
    "planFingerprint",
    "outputNodeIds",
    "admissionBinding",
    "status",
    "sequence",
    "nodes",
    "updatedAt",
  ]);
  if (checkpoint.schemaVersion !== COOGO_EXECUTION_CHECKPOINT_V1_VERSION) {
    throw new Error(
      `checkpoint schemaVersion must be ${COOGO_EXECUTION_CHECKPOINT_V1_VERSION}`,
    );
  }
  const planId = checkpointString(checkpoint.planId, "checkpoint.planId");
  const runId = checkpointString(checkpoint.runId, "checkpoint.runId");
  const projectId = checkpointString(
    checkpoint.projectId,
    "checkpoint.projectId",
  );
  const planFingerprint = checkpointString(
    checkpoint.planFingerprint,
    "checkpoint.planFingerprint",
  );
  const updatedAt = checkpointString(
    checkpoint.updatedAt,
    "checkpoint.updatedAt",
  );
  if (
    !Number.isInteger(checkpoint.sequence) ||
    (checkpoint.sequence as number) < 0
  ) {
    throw new Error("checkpoint sequence must be a non-negative integer");
  }
  if (
    !CHECKPOINT_PLAN_STATUSES.has(checkpoint.status as CoogoPlanExecutionStatus)
  ) {
    throw new Error(
      `checkpoint status is invalid: ${String(checkpoint.status)}`,
    );
  }
  const outputNodeIds = parseCheckpointOutputNodeIds(checkpoint.outputNodeIds);
  const admissionBinding =
    checkpoint.admissionBinding === undefined
      ? undefined
      : parseCheckpointAdmissionBinding(checkpoint.admissionBinding, projectId);
  const expectedPath = admissionBinding
    ? `/manifest/pages/${admissionBinding.target.pageId}/title`
    : undefined;
  const rawNodes = checkpointRecord(checkpoint.nodes, "checkpoint.nodes");
  const nodes: Record<string, CoogoExecutionCheckpointNodeV1> = {};
  for (const [nodeKey, rawNode] of Object.entries(rawNodes)) {
    nodes[nodeKey] = parseCheckpointNode(
      rawNode,
      nodeKey,
      admissionBinding,
      expectedPath,
    );
  }

  return {
    schemaVersion: COOGO_EXECUTION_CHECKPOINT_V1_VERSION,
    planId,
    runId,
    projectId,
    planFingerprint,
    outputNodeIds,
    ...(admissionBinding ? { admissionBinding } : {}),
    status: checkpoint.status as CoogoPlanExecutionStatus,
    sequence: checkpoint.sequence as number,
    nodes,
    updatedAt,
  };
}

export function validateCoogoExecutionTransition(
  transition: CoogoExecutionTransition,
): { valid: boolean; errors: string[] } {
  const errors: string[] = [];
  if (
    !Number.isInteger(transition.expectedCheckpointRevision) ||
    transition.expectedCheckpointRevision < 0
  ) {
    errors.push("expectedCheckpointRevision must be a non-negative integer");
  }
  if (transition.event.schemaVersion !== COOGO_EXECUTION_EVENT_V2_VERSION) {
    errors.push(
      `transition event must use ${COOGO_EXECUTION_EVENT_V2_VERSION}`,
    );
  } else {
    errors.push(...validateCoogoExecutionEvent(transition.event).errors);
  }
  if (transition.expectedCheckpointRevision === 0) {
    if (transition.event.type !== "plan.accepted") {
      errors.push("revision 0 transition event must be plan.accepted");
    }
    if (transition.event.sequence !== 0) {
      errors.push("revision 0 transition event sequence must be 0");
    }
    if (transition.nextCheckpoint.sequence !== 0) {
      errors.push("revision 0 transition checkpoint sequence must be 0");
    }
  }
  let checkpoint: CoogoExecutionCheckpointV1 | undefined;
  try {
    checkpoint = parseCoogoExecutionCheckpointV1(transition.nextCheckpoint);
  } catch (cause) {
    errors.push(
      cause instanceof Error
        ? cause.message
        : "transition checkpoint is invalid",
    );
  }
  if (checkpoint) {
    if (transition.event.planId !== checkpoint.planId) {
      errors.push("transition planId must match checkpoint");
    }
    if (transition.event.runId !== checkpoint.runId) {
      errors.push("transition runId must match checkpoint");
    }
    if (transition.event.projectId !== checkpoint.projectId) {
      errors.push("transition projectId must match checkpoint");
    }
    if (transition.event.sequence !== checkpoint.sequence) {
      errors.push("transition sequence must match checkpoint");
    }
  }
  if (transition.lease !== undefined) {
    if (!transition.lease.leaseId?.trim()) {
      errors.push("transition leaseId is required");
    }
    if (
      !Number.isInteger(transition.lease.fencingToken) ||
      transition.lease.fencingToken < 0
    ) {
      errors.push("transition fencingToken must be a non-negative integer");
    }
  }
  return { valid: errors.length === 0, errors };
}

function parseCheckpointOutputNodeIds(value: unknown): string[] | null {
  if (value === null) return null;
  if (!Array.isArray(value)) {
    throw new Error("checkpoint.outputNodeIds must be an array or null");
  }
  return value.map((nodeId, index) =>
    checkpointString(nodeId, `checkpoint.outputNodeIds[${index}]`),
  );
}

function parseCheckpointAdmissionBinding(
  value: unknown,
  projectId: string,
): CoogoCheckpointAdmissionBindingV1 {
  const binding = checkpointRecord(value, "checkpoint.admissionBinding");
  checkpointOnlyKeys(binding, "checkpoint.admissionBinding", [
    "gateNodeId",
    "confirmationId",
    "expiresAt",
    "principalBinding",
    "sessionBinding",
    "target",
    "duplicatePolicy",
    "durability",
  ]);
  const target = parseCheckpointTarget(
    binding.target,
    "checkpoint.admissionBinding.target",
  );
  if (target.projectId !== projectId) {
    throw new Error(
      "checkpoint admission target must match checkpoint project",
    );
  }
  if (binding.principalBinding !== "authenticated-owner") {
    throw new Error("checkpoint admission principalBinding is invalid");
  }
  if (binding.sessionBinding !== "verified-jwt-session") {
    throw new Error("checkpoint admission sessionBinding is invalid");
  }
  if (binding.duplicatePolicy !== "reject") {
    throw new Error("checkpoint admission duplicatePolicy is invalid");
  }
  if (binding.durability !== "durable") {
    throw new Error("checkpoint admission durability is invalid");
  }
  return {
    gateNodeId: checkpointString(
      binding.gateNodeId,
      "checkpoint.admissionBinding.gateNodeId",
    ),
    confirmationId: checkpointString(
      binding.confirmationId,
      "checkpoint.admissionBinding.confirmationId",
    ),
    expiresAt: checkpointString(
      binding.expiresAt,
      "checkpoint.admissionBinding.expiresAt",
    ),
    principalBinding: "authenticated-owner",
    sessionBinding: "verified-jwt-session",
    target,
    duplicatePolicy: "reject",
    durability: "durable",
  };
}

function parseCheckpointTarget(
  value: unknown,
  path: string,
): CoogoCheckpointAdmissionBindingV1["target"] {
  const target = checkpointRecord(value, path);
  checkpointOnlyKeys(target, path, ["kind", "projectId", "pageId"]);
  if (target.kind !== "studio-page") {
    throw new Error(`${path}.kind must be studio-page`);
  }
  return {
    kind: "studio-page",
    projectId: checkpointString(target.projectId, `${path}.projectId`),
    pageId: checkpointString(target.pageId, `${path}.pageId`),
  };
}

function parseCheckpointNode(
  value: unknown,
  nodeKey: string,
  admissionBinding: CoogoCheckpointAdmissionBindingV1 | undefined,
  expectedPath: string | undefined,
): CoogoExecutionCheckpointNodeV1 {
  const path = `checkpoint.nodes.${nodeKey}`;
  const node = checkpointRecord(value, path);
  checkpointOnlyKeys(node, path, [
    "nodeId",
    "status",
    "error",
    "suspension",
    "telemetry",
    "checkpointOutput",
  ]);
  const nodeId = checkpointString(node.nodeId, `${path}.nodeId`);
  if (nodeId !== nodeKey) {
    throw new Error(`${path}.nodeId must match its node key`);
  }
  if (!CHECKPOINT_NODE_STATUSES.has(node.status as CoogoNodeExecutionStatus)) {
    throw new Error(`${nodeId}: checkpoint node status is invalid`);
  }
  const result: CoogoExecutionCheckpointNodeV1 = {
    nodeId,
    status: node.status as CoogoNodeExecutionStatus,
  };
  if (node.error !== undefined)
    result.error = parseCheckpointError(node.error, path);
  if (node.telemetry !== undefined) {
    result.telemetry = parseCheckpointTelemetry(node.telemetry, path);
  }
  if (node.suspension !== undefined) {
    if (!admissionBinding) {
      throw new Error(`${nodeId}: checkpoint suspension is not allowed`);
    }
    if (admissionBinding.gateNodeId !== nodeId) {
      throw new Error(
        "checkpoint admission gateNodeId must match owning gate node",
      );
    }
    result.suspension = parseCheckpointSuspension(
      node.suspension,
      admissionBinding,
      nodeId,
      path,
    );
  }
  if (node.checkpointOutput !== undefined) {
    result.checkpointOutput = parseCheckpointOutput(
      node.checkpointOutput,
      nodeId,
      expectedPath,
    );
  }
  return result;
}

function parseCheckpointError(
  value: unknown,
  nodePath: string,
): CoogoSerializedExecutionError {
  const path = `${nodePath}.error`;
  const error = checkpointRecord(value, path);
  checkpointOnlyKeys(error, path, ["name", "message", "code", "retryable"]);
  if (error.retryable !== undefined && typeof error.retryable !== "boolean") {
    throw new Error(`${path}.retryable must be boolean`);
  }
  return {
    name: checkpointString(error.name, `${path}.name`),
    message: checkpointString(error.message, `${path}.message`),
    ...(error.code === undefined
      ? {}
      : { code: checkpointString(error.code, `${path}.code`) }),
    ...(error.retryable === undefined
      ? {}
      : { retryable: error.retryable as boolean }),
  };
}

function parseCheckpointTelemetry(
  value: unknown,
  nodePath: string,
): NonNullable<CoogoExecutionCheckpointNodeV1["telemetry"]> {
  const path = `${nodePath}.telemetry`;
  const telemetry = checkpointRecord(value, path);
  checkpointOnlyKeys(telemetry, path, [
    "attempts",
    "durationMs",
    "cost",
    "inputTokens",
    "outputTokens",
    "totalTokens",
  ]);
  const result: NonNullable<CoogoExecutionCheckpointNodeV1["telemetry"]> = {
    attempts: checkpointNonNegativeNumber(
      telemetry.attempts,
      `${path}.attempts`,
    ),
    durationMs: checkpointNonNegativeNumber(
      telemetry.durationMs,
      `${path}.durationMs`,
    ),
  };
  for (const key of [
    "cost",
    "inputTokens",
    "outputTokens",
    "totalTokens",
  ] as const) {
    if (telemetry[key] !== undefined) {
      result[key] = checkpointNonNegativeNumber(
        telemetry[key],
        `${path}.${key}`,
      );
    }
  }
  return result;
}

function parseCheckpointSuspension(
  value: unknown,
  binding: CoogoCheckpointAdmissionBindingV1,
  owningNodeId: string,
  nodePath: string,
): CoogoCheckpointGateSuspensionV1 {
  const path = `${nodePath}.suspension`;
  const suspension = checkpointRecord(value, path);
  checkpointOnlyKeys(suspension, path, [
    "kind",
    "reason",
    "gateNodeId",
    "confirmationId",
    "expiresAt",
    "target",
  ]);
  if (suspension.kind !== "human-gate") {
    throw new Error(`${path}.kind must be human-gate`);
  }
  const target = parseCheckpointTarget(suspension.target, `${path}.target`);
  const result: CoogoCheckpointGateSuspensionV1 = {
    kind: "human-gate",
    reason: checkpointString(suspension.reason, `${path}.reason`),
    gateNodeId: checkpointString(suspension.gateNodeId, `${path}.gateNodeId`),
    confirmationId: checkpointString(
      suspension.confirmationId,
      `${path}.confirmationId`,
    ),
    expiresAt: checkpointString(suspension.expiresAt, `${path}.expiresAt`),
    target,
  };
  if (result.gateNodeId !== owningNodeId) {
    throw new Error(
      "checkpoint suspension gateNodeId must match owning gate node",
    );
  }
  if (
    result.gateNodeId !== binding.gateNodeId ||
    result.confirmationId !== binding.confirmationId ||
    result.expiresAt !== binding.expiresAt ||
    result.target.kind !== binding.target.kind ||
    result.target.projectId !== binding.target.projectId ||
    result.target.pageId !== binding.target.pageId
  ) {
    throw new Error("checkpoint gate suspension must match admission binding");
  }
  return result;
}

function parseCheckpointOutput(
  value: unknown,
  nodeId: string,
  expectedPath: string | undefined,
): CoogoCheckpointSafeOutputV1 {
  if (EXACT_IMPACT_NODE_IDS.has(nodeId)) {
    return parseCheckpointExactImpactOutput(value, nodeId, expectedPath);
  }
  if (COMPENSATION_NODE_IDS.has(nodeId)) {
    return parseCheckpointCompensationOutput(value, nodeId);
  }
  throw new Error(`${nodeId}: checkpointOutput is not allowed`);
}

function parseCheckpointExactImpactOutput(
  value: unknown,
  nodeId: string,
  expectedPath: string | undefined,
): CoogoCheckpointExactImpactOutputV1 {
  const path = `checkpoint.nodes.${nodeId}.checkpointOutput`;
  const output = checkpointRecord(value, path);
  checkpointOnlyKeys(output, path, [
    "kind",
    "mode",
    "observations",
    "verified",
    "fingerprint",
    "capturedAt",
  ]);
  if (output.kind !== "exact-impact") {
    throw new Error(`${nodeId}: checkpointOutput must be exact-impact`);
  }
  if (output.mode !== "capture" && output.mode !== "assert") {
    throw new Error(`${path}.mode is invalid`);
  }
  if (!Array.isArray(output.observations) || output.observations.length === 0) {
    throw new Error(`${path}.observations must be non-empty`);
  }
  if (!expectedPath) {
    throw new Error(
      `${nodeId}: exact-impact output requires an admission target`,
    );
  }
  const observations = output.observations.map((observation, index) =>
    parseCheckpointObservation(
      observation,
      `${path}.observations[${index}]`,
      expectedPath,
    ),
  );
  if (typeof output.verified !== "boolean") {
    throw new Error(`${path}.verified must be boolean`);
  }
  return {
    kind: "exact-impact",
    mode: output.mode,
    observations,
    verified: output.verified,
    fingerprint: checkpointString(output.fingerprint, `${path}.fingerprint`),
    capturedAt: checkpointString(output.capturedAt, `${path}.capturedAt`),
  };
}

function parseCheckpointObservation(
  value: unknown,
  path: string,
  expectedPath: string,
): CoogoCheckpointObservationV1 {
  const observation = checkpointRecord(value, path);
  const observationPath = checkpointString(observation.path, `${path}.path`);
  if (observationPath !== expectedPath) {
    throw new Error(`${path}.path must match the admitted page-title target`);
  }
  if (observation.presence === "absent") {
    checkpointOnlyKeys(observation, path, ["path", "presence"]);
    return { path: observationPath, presence: "absent" };
  }
  if (observation.presence !== "present") {
    throw new Error(`${path}.presence is invalid`);
  }
  if (observation.valueKind === "undefined") {
    checkpointOnlyKeys(observation, path, ["path", "presence", "valueKind"]);
    return {
      path: observationPath,
      presence: "present",
      valueKind: "undefined",
    };
  }
  if (observation.valueKind !== "json-primitive") {
    throw new Error(`${path}.valueKind is invalid`);
  }
  checkpointOnlyKeys(observation, path, [
    "path",
    "presence",
    "valueKind",
    "value",
  ]);
  if (!isCheckpointJsonPrimitive(observation.value)) {
    throw new Error(`${path}.value must be a JSON primitive`);
  }
  return {
    path: observationPath,
    presence: "present",
    valueKind: "json-primitive",
    value: observation.value,
  };
}

function parseCheckpointCompensationOutput(
  value: unknown,
  nodeId: string,
): CoogoCheckpointCompensationOutputV1 {
  const path = `checkpoint.nodes.${nodeId}.checkpointOutput`;
  const output = checkpointRecord(value, path);
  checkpointOnlyKeys(output, path, ["kind", "disposition", "verified"]);
  if (output.kind !== "compensation-result") {
    throw new Error(`${nodeId}: checkpointOutput must be compensation-result`);
  }
  if (
    output.disposition !== "already-restored" &&
    output.disposition !== "owned-undo"
  ) {
    throw new Error(`${path}.disposition is invalid`);
  }
  if (output.verified !== true) {
    throw new Error(`${path}.verified must be true`);
  }
  return {
    kind: "compensation-result",
    disposition: output.disposition,
    verified: true,
  };
}

function checkpointRecord(
  value: unknown,
  path: string,
): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new Error(`${path} must be an object`);
  }
  return value as Record<string, unknown>;
}

function checkpointOnlyKeys(
  value: Record<string, unknown>,
  path: string,
  allowed: readonly string[],
): void {
  const allowedKeys = new Set(allowed);
  for (const key of Object.keys(value)) {
    if (!allowedKeys.has(key)) {
      throw new Error(`${path} contains unknown field: ${key}`);
    }
  }
}

function checkpointString(value: unknown, path: string): string {
  if (typeof value !== "string" || !value.trim()) {
    throw new Error(`${path} must be a non-empty string`);
  }
  return value;
}

function checkpointNonNegativeNumber(value: unknown, path: string): number {
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0) {
    throw new Error(`${path} must be a non-negative number`);
  }
  return value;
}

function isCheckpointJsonPrimitive(
  value: unknown,
): value is CoogoCheckpointJsonPrimitive {
  return (
    value === null ||
    typeof value === "string" ||
    typeof value === "boolean" ||
    (typeof value === "number" && Number.isFinite(value))
  );
}

export interface CoogoSubplanExecutionOptions {
  outputNodeIds?: string[];
  resumeData?: Record<string, unknown>;
}

export interface CoogoNodeHandlerContext {
  plan: CoogoReadableOrchestrationPlan;
  node: CoogoOrchestrationNode | CoogoOrchestrationNodeV2;
  signal: AbortSignal;
  attempt: number;
  dependencyOutputs: Readonly<Record<string, unknown>>;
  resumeData?: unknown;
  executeSubplan(
    plan: CoogoOrchestrationPlan,
    options?: CoogoSubplanExecutionOptions,
  ): Promise<CoogoExecutionResult>;
}

export type CoogoNodeHandler = (
  context: CoogoNodeHandlerContext,
) => CoogoNodeHandlerResult | Promise<CoogoNodeHandlerResult>;

export type CoogoHandlerMap = Partial<
  Record<CoogoOrchestrationNodeKind, CoogoNodeHandler>
>;

export type CoogoMockDefinition = CoogoNodeHandler | CoogoNodeHandlerResult;

export interface CoogoExecutorDependencies {
  handlers?: CoogoHandlerMap | CoogoOrchestrationHandlerRegistry;
  journal?: CoogoExecutionJournal;
  leaseController?: CoogoExecutionLeaseController;
  now?: () => number;
  maxAttempts?: number;
  retryDelayMs?: number;
  onEvent?: (
    event: CoogoExecutionEvent | CoogoExecutionEventV2,
  ) => void | Promise<void>;
}

export interface CoogoExecuteOptions {
  signal?: AbortSignal;
  outputNodeIds?: string[];
  resumeData?: Record<string, unknown>;
  mocks?: Record<string, CoogoMockDefinition>;
  resume?: boolean;
}

export interface CoogoExecutionResult {
  planId: string;
  runId: string;
  projectId: string;
  status: CoogoPlanExecutionStatus;
  outputs: Record<string, unknown>;
  nodes: Record<string, CoogoNodeExecutionRecord>;
  events: Array<CoogoExecutionEvent | CoogoExecutionEventV2>;
  suspension?: {
    nodeId: string;
    detail: CoogoSuspension;
  };
}

export interface CoogoLoopBodyContext extends CoogoNodeHandlerContext {
  iteration: number;
  previousOutput?: unknown;
}

export interface CoogoLoopBodyResult extends CoogoNodeHandlerResult {
  continue?: boolean;
}

export interface CoogoLoopHandlerOptions {
  maxIterations: number;
  body(
    context: CoogoLoopBodyContext,
  ): CoogoLoopBodyResult | Promise<CoogoLoopBodyResult>;
  shouldContinue?: (
    result: CoogoLoopBodyResult,
    context: CoogoLoopBodyContext,
  ) => boolean | Promise<boolean>;
}

export type CoogoSubplanResolver = (context: CoogoNodeHandlerContext) =>
  | CoogoOrchestrationPlan
  | Promise<CoogoOrchestrationPlan>
  | {
      plan: CoogoOrchestrationPlan;
      outputNodeIds?: string[];
    }
  | Promise<{
      plan: CoogoOrchestrationPlan;
      outputNodeIds?: string[];
    }>;

export class CoogoExecutionError extends Error {
  readonly code: string;
  readonly retryable: boolean;

  constructor(message: string, code = "execution_failed", retryable = false) {
    super(message);
    this.name = "CoogoExecutionError";
    this.code = code;
    this.retryable = retryable;
  }
}

export class CoogoExecutionTimeoutError extends CoogoExecutionError {
  constructor(nodeId: string, timeoutMs: number) {
    super(
      `Node ${nodeId} exceeded its ${timeoutMs}ms timeout`,
      "node_timeout",
      true,
    );
    this.name = "CoogoExecutionTimeoutError";
  }
}

export class CoogoExecutionCancelledError extends CoogoExecutionError {
  constructor(reason = "Execution cancelled") {
    super(reason, "execution_cancelled", false);
    this.name = "CoogoExecutionCancelledError";
  }
}

export class CoogoOrchestrationValidationError extends CoogoExecutionError {
  readonly errors: string[];

  constructor(errors: string[]) {
    super(
      `Invalid Coogo orchestration plan: ${errors.join("; ")}`,
      "invalid_plan",
      false,
    );
    this.name = "CoogoOrchestrationValidationError";
    this.errors = errors;
  }
}

/** Legacy save-only journal retained until atomic execution lands in Task 2. */
export class LegacyInMemoryCoogoExecutionSnapshotJournal implements LegacyCoogoExecutionSnapshotJournal {
  private readonly snapshots = new Map<string, CoogoExecutionSnapshot>();

  async load(runId: string): Promise<CoogoExecutionSnapshot | undefined> {
    const snapshot = this.snapshots.get(runId);
    return snapshot ? cloneSnapshot(snapshot) : undefined;
  }

  async save(snapshot: CoogoExecutionSnapshot): Promise<void> {
    this.snapshots.set(snapshot.runId, cloneSnapshot(snapshot));
  }

  clear(runId?: string): void {
    if (runId) this.snapshots.delete(runId);
    else this.snapshots.clear();
  }
}

interface InMemoryExecutionRun {
  checkpointRevision: number;
  checkpoint: CoogoExecutionCheckpointV1;
  events: CoogoExecutionEventV2[];
  transitions: Map<
    string,
    { bytes: string; receipt: CoogoExecutionTransitionReceipt }
  >;
}

/** Atomic package-local journal used for deterministic executor tests and adapters. */
export class InMemoryCoogoExecutionJournal implements CoogoExecutionJournal {
  private readonly runs = new Map<string, InMemoryExecutionRun>();

  async load(runId: string): Promise<CoogoLoadedExecution | undefined> {
    const run = this.runs.get(runId);
    if (!run) return undefined;
    return {
      checkpointRevision: run.checkpointRevision,
      snapshot: snapshotFromCheckpoint(run.checkpoint, run.events),
    };
  }

  async commitTransition(
    transition: CoogoExecutionTransition,
  ): Promise<CoogoExecutionTransitionReceipt> {
    const validation = validateCoogoExecutionTransition(transition);
    if (!validation.valid) {
      throw new CoogoExecutionError(
        `Invalid execution transition: ${validation.errors.join("; ")}`,
        "invalid_transition",
      );
    }
    const runId = transition.event.runId;
    const current = this.runs.get(runId);
    const transitionBytes = stableStringify(transition);
    const replay = current?.transitions.get(transition.event.eventId);
    if (replay) {
      if (replay.bytes !== transitionBytes) {
        throw new CoogoExecutionError(
          `Event ${transition.event.eventId} was reused with different transition bytes`,
          "journal_transition_conflict",
        );
      }
      return { ...replay.receipt, replayed: true };
    }

    const currentRevision = current?.checkpointRevision ?? 0;
    const lastSequence = current?.events[current.events.length - 1]?.sequence ?? -1;
    if (transition.expectedCheckpointRevision !== currentRevision) {
      throw new CoogoExecutionError(
        `Expected checkpoint revision ${transition.expectedCheckpointRevision}, found ${currentRevision}`,
        "journal_revision_conflict",
      );
    }
    if (transition.event.sequence !== lastSequence + 1) {
      throw new CoogoExecutionError(
        `Expected event sequence ${lastSequence + 1}, found ${transition.event.sequence}`,
        "journal_sequence_conflict",
      );
    }

    const nextCheckpoint = parseCoogoExecutionCheckpointV1(
      structuredClone(transition.nextCheckpoint),
    );
    const event = structuredClone(transition.event);
    const receipt: CoogoExecutionTransitionReceipt = {
      checkpointRevision: currentRevision + 1,
      eventId: event.eventId,
      sequence: event.sequence,
      replayed: false,
    };
    const transitions = new Map(current?.transitions ?? []);
    transitions.set(event.eventId, { bytes: transitionBytes, receipt });
    this.runs.set(runId, {
      checkpointRevision: receipt.checkpointRevision,
      checkpoint: nextCheckpoint,
      events: [...(current?.events ?? []), event],
      transitions,
    });
    return receipt;
  }
}

/** Registry supports kind-wide and operation-specific handlers. */
export class CoogoOrchestrationHandlerRegistry {
  private readonly handlers = new Map<string, CoogoNodeHandler>();

  constructor(handlers: CoogoHandlerMap = {}) {
    for (const [kind, handler] of Object.entries(handlers)) {
      if (handler) this.register(kind as CoogoOrchestrationNodeKind, handler);
    }
  }

  register(
    kind: CoogoOrchestrationNodeKind,
    handler: CoogoNodeHandler,
    operationName?: string,
  ): this {
    this.handlers.set(handlerKey(kind, operationName), handler);
    return this;
  }

  resolve(
    node: CoogoOrchestrationNode | CoogoOrchestrationNodeV2,
  ): CoogoNodeHandler | undefined {
    const operationHandler = node.operation?.name
      ? this.handlers.get(handlerKey(node.kind, node.operation.name))
      : undefined;
    return operationHandler ?? this.handlers.get(handlerKey(node.kind));
  }
}

interface ActiveExecution {
  controller: AbortController;
  reason?: string;
}

interface ExecutionState {
  plan: CoogoReadableOrchestrationPlan;
  scope: Set<string>;
  outputNodeIds: string[] | null;
  snapshot: CoogoExecutionSnapshot;
  options: CoogoExecuteOptions;
  controller: AbortController;
  checkpointRevision: number;
  lease?: CoogoExecutionLease;
  leaseDisposition?: "suspended" | "terminal";
}

interface NodeRunOutcome {
  node: CoogoOrchestrationNode | CoogoOrchestrationNodeV2;
  status: "completed" | "failed" | "cancelled" | "suspended";
  output?: unknown;
  error?: CoogoSerializedExecutionError;
  suspension?: CoogoSuspension;
  telemetry: CoogoNodeTelemetry;
}

export class CoogoOrchestrationExecutor {
  readonly registry: CoogoOrchestrationHandlerRegistry;
  readonly journal: CoogoExecutionJournal;
  private readonly now: () => number;
  private readonly maxAttempts: number;
  private readonly retryDelayMs: number;
  private readonly onEvent?: CoogoExecutorDependencies["onEvent"];
  private readonly leaseController?: CoogoExecutionLeaseController;
  private readonly activeExecutions = new Map<string, ActiveExecution>();

  constructor(dependencies: CoogoExecutorDependencies = {}) {
    this.registry =
      dependencies.handlers instanceof CoogoOrchestrationHandlerRegistry
        ? dependencies.handlers
        : new CoogoOrchestrationHandlerRegistry(dependencies.handlers);
    this.journal = dependencies.journal ?? new InMemoryCoogoExecutionJournal();
    this.now = dependencies.now ?? Date.now;
    this.maxAttempts = Math.max(1, Math.floor(dependencies.maxAttempts ?? 2));
    this.retryDelayMs = Math.max(0, dependencies.retryDelayMs ?? 0);
    this.onEvent = dependencies.onEvent;
    this.leaseController = dependencies.leaseController;
  }

  cancel(runId: string, reason = "Execution cancelled by caller"): boolean {
    const active = this.activeExecutions.get(runId);
    if (!active) return false;
    active.reason = reason;
    active.controller.abort(new CoogoExecutionCancelledError(reason));
    return true;
  }

  async cancelPersisted(
    plan: CoogoReadableOrchestrationPlan,
    reason = "Execution cancelled by caller",
  ): Promise<CoogoExecutionResult> {
    const validation = validateCoogoOrchestrationPlan(plan);
    if (!validation.valid) {
      throw new CoogoOrchestrationValidationError(validation.errors);
    }
    if (this.activeExecutions.has(plan.runId)) {
      throw new CoogoExecutionError(
        `Run ${plan.runId} is already executing`,
        "run_already_active",
      );
    }

    const controller = new AbortController();
    this.activeExecutions.set(plan.runId, { controller, reason });
    try {
      const existing = await this.journal.load(plan.runId);
      if (!existing) {
        throw new CoogoExecutionError(
          `Run ${plan.runId} has no persisted execution`,
          "persisted_run_not_found",
        );
      }
      const outputNodeIds = existing.snapshot.outputNodeIds;
      const scope = resolveExecutionScope(plan, outputNodeIds);
      const planFingerprint = fingerprintPlan(plan, outputNodeIds);
      const snapshot = validateAndPrepareSnapshot(
        existing.snapshot,
        plan,
        planFingerprint,
        outputNodeIds,
        undefined,
        this.now,
      );
      if (
        snapshot.status === "completed" ||
        snapshot.status === "failed" ||
        snapshot.status === "cancelled"
      ) {
        return buildExecutionResult(snapshot, outputNodeIds);
      }
      if (snapshot.status !== "suspended") {
        throw new CoogoExecutionError(
          `Run ${plan.runId} is not suspended`,
          "persisted_run_not_suspended",
        );
      }

      const state: ExecutionState = {
        plan,
        scope,
        outputNodeIds,
        snapshot,
        options: {},
        controller,
        checkpointRevision: existing.checkpointRevision,
      };
      const error = serializeError(new CoogoExecutionCancelledError(reason));
      for (const node of plan.nodes) {
        if (!scope.has(node.id)) continue;
        const record = snapshot.nodes[node.id];
        if (
          !record ||
          (record.status !== "pending" &&
            record.status !== "running" &&
            record.status !== "suspended")
        ) {
          continue;
        }
        await this.emit(
          state,
          "node.cancelled",
          { status: "cancelled", error },
          node.id,
          (draft) => {
            draft.nodes[node.id] = {
              nodeId: node.id,
              status: "cancelled",
              error,
              telemetry: record.telemetry ?? {
                attempts: record.status === "pending" ? 0 : 1,
                durationMs: 0,
              },
            };
          },
        );
      }
      await this.emit(
        state,
        "plan.cancelled",
        { reason },
        undefined,
        (draft) => {
          draft.status = "cancelled";
        },
      );
      return buildExecutionResult(snapshot, outputNodeIds);
    } finally {
      this.activeExecutions.delete(plan.runId);
    }
  }

  async execute(
    plan: CoogoReadableOrchestrationPlan,
    options: CoogoExecuteOptions = {},
  ): Promise<CoogoExecutionResult> {
    const validation = validateCoogoOrchestrationPlan(plan);
    if (!validation.valid) {
      throw new CoogoOrchestrationValidationError(validation.errors);
    }
    if (this.activeExecutions.has(plan.runId)) {
      throw new CoogoExecutionError(
        `Run ${plan.runId} is already executing`,
        "run_already_active",
      );
    }

    const outputNodeIds = normalizeOutputNodeIds(plan, options.outputNodeIds);
    const scope = resolveExecutionScope(plan, outputNodeIds);
    const planFingerprint = fingerprintPlan(plan, outputNodeIds);
    const controller = new AbortController();
    const active: ActiveExecution = { controller };
    this.activeExecutions.set(plan.runId, active);
    const detachExternalAbort = forwardAbort(options.signal, controller);
    let state: ExecutionState | undefined;
    let leaseRenewalTimer: ReturnType<typeof setInterval> | undefined;

    try {
      const existing =
        options.resume === false
          ? undefined
          : await this.journal.load(plan.runId);
      const resumingSuspended = existing?.snapshot.status === "suspended";
      const snapshot = existing
        ? validateAndPrepareSnapshot(
            existing.snapshot,
            plan,
            planFingerprint,
            outputNodeIds,
            options.resumeData,
            this.now,
          )
        : createSnapshot(plan, planFingerprint, outputNodeIds, scope, this.now);

      if (
        existing &&
        (snapshot.status === "completed" ||
          snapshot.status === "failed" ||
          snapshot.status === "cancelled")
      ) {
        return buildExecutionResult(snapshot, outputNodeIds);
      }
      if (
        existing &&
        snapshot.status === "suspended" &&
        !hasResumableNode(snapshot, options.resumeData)
      ) {
        if (hasResumeData(options.resumeData)) {
          throw new CoogoExecutionError(
            `Resume data does not name a suspended node in run ${plan.runId}`,
            "resume_node_not_suspended",
          );
        }
        return buildExecutionResult(snapshot, outputNodeIds);
      }

      state = {
        plan,
        scope,
        outputNodeIds,
        snapshot,
        options,
        controller,
        checkpointRevision: existing?.checkpointRevision ?? 0,
      };
      if (resumingSuspended && this.leaseController) {
        try {
          state.lease = await this.leaseController.acquire({
            plan,
            snapshot: cloneSnapshot(snapshot),
            resumeData: options.resumeData ?? {},
            signal: controller.signal,
          });
          assertLeaseHasHardExpiryMargin(state.lease, this.now());
          leaseRenewalTimer = setInterval(() => {
            void this.renewExecutionLease(state!);
          }, 10_000);
        } catch (cause) {
          const error = executionLeaseLost(cause);
          controller.abort(error);
          throw error;
        }
      }
      if (resumingSuspended) {
        try {
          validateHumanGateResumeAttempt(
            plan,
            snapshot,
            options.resumeData,
            this.now(),
          );
        } catch (error) {
          if (state.lease) state.leaseDisposition = "suspended";
          throw error;
        }
      }
      await this.emit(
        state,
        existing ? "plan.resumed" : "plan.accepted",
        { outputNodeIds },
        undefined,
        (draft) => {
          draft.status = "running";
        },
      );
      await this.runDag(state, active);
      return buildExecutionResult(snapshot, outputNodeIds);
    } finally {
      if (leaseRenewalTimer) clearInterval(leaseRenewalTimer);
      if (state?.lease && state.leaseDisposition && this.leaseController) {
        try {
          await this.leaseController.release(
            state.lease,
            state.leaseDisposition,
          );
        } catch {
          // Lease expiry is authoritative; release is best-effort after receipt.
        }
      }
      detachExternalAbort();
      this.activeExecutions.delete(plan.runId);
    }
  }

  private async runDag(
    state: ExecutionState,
    active: ActiveExecution,
  ): Promise<void> {
    const { plan, snapshot, scope, controller } = state;
    const order = new Map(plan.nodes.map((node, index) => [node.id, index]));
    const scopedNodes = plan.nodes.filter((node) => scope.has(node.id));

    while (true) {
      if (controller.signal.aborted) {
        if (isExecutionLeaseLoss(controller.signal.reason)) {
          const leaseError = controller.signal.reason;
          const failedRoot = scopedNodes.find((node) => {
            const status = snapshot.nodes[node.id]?.status;
            return status === "pending" || status === "running";
          });
          if (!failedRoot) {
            const retainedSuspensions = scopedNodes
              .map((node) => snapshot.nodes[node.id])
              .filter(
                (record): record is CoogoNodeExecutionRecord =>
                  record?.status === "suspended",
              );
            if (retainedSuspensions.length > 0) {
              await this.emit(
                state,
                "plan.suspended",
                {
                  nodeId: retainedSuspensions[0].nodeId,
                  suspendedNodeIds: retainedSuspensions.map(
                    (record) => record.nodeId,
                  ),
                },
                undefined,
                (draft) => {
                  draft.status = "suspended";
                },
              );
              return;
            }
          }
          if (failedRoot) {
            const priorStatus = snapshot.nodes[failedRoot.id]?.status;
            const error = serializeError(leaseError);
            const telemetry = {
              attempts: priorStatus === "running" ? 1 : 0,
              durationMs: 0,
            };
            await this.emit(
              state,
              "node.failed",
              { status: "failed", error, ...telemetry },
              failedRoot.id,
              (draft) => {
                draft.nodes[failedRoot.id] = {
                  nodeId: failedRoot.id,
                  status: "failed",
                  error,
                  telemetry,
                };
              },
            );
          }
          await this.cancelPendingNodes(
            state,
            leaseError.message,
            undefined,
            true,
          );
          break;
        }
        await this.cancelPendingNodes(state, active.reason, undefined, true);
        await this.emit(
          state,
          "plan.cancelled",
          { reason: active.reason ?? abortReason(controller.signal) },
          undefined,
          (draft) => {
            draft.status = "cancelled";
          },
        );
        return;
      }

      await this.propagateUnsatisfiedDependencies(state, scopedNodes);
      const pending = scopedNodes.filter(
        (node) => snapshot.nodes[node.id]?.status === "pending",
      );
      if (pending.length === 0) {
        const suspendedRecords = scopedNodes
          .map((node) => snapshot.nodes[node.id])
          .filter(
            (record): record is CoogoNodeExecutionRecord =>
              record?.status === "suspended",
          );
        const hasFailure = scopedNodes.some(
          (node) => snapshot.nodes[node.id]?.status === "failed",
        );
        if (suspendedRecords.length > 0 && !hasFailure) {
          await this.emit(
            state,
            "plan.suspended",
            {
              nodeId: suspendedRecords[0].nodeId,
              suspendedNodeIds: suspendedRecords.map((record) => record.nodeId),
            },
            undefined,
            (draft) => {
              draft.status = "suspended";
            },
          );
          return;
        }
        break;
      }

      const ready = pending.filter((node) =>
        executionDependencies(plan, node).every((dependency) =>
          dependencyIsSatisfied(
            snapshot.nodes[dependency.nodeId]?.status,
            dependency.when,
          ),
        ),
      );
      if (ready.length === 0) {
        const suspendedRecords = scopedNodes
          .map((node) => snapshot.nodes[node.id])
          .filter(
            (record): record is CoogoNodeExecutionRecord =>
              record?.status === "suspended",
          );
        const hasFailure = scopedNodes.some(
          (node) => snapshot.nodes[node.id]?.status === "failed",
        );
        if (hasFailure) break;
        if (suspendedRecords.length > 0) {
          await this.emit(
            state,
            "plan.suspended",
            {
              nodeId: suspendedRecords[0].nodeId,
              suspendedNodeIds: suspendedRecords.map((record) => record.nodeId),
            },
            undefined,
            (draft) => {
              bindSnapshotAdmissionToNode(
                draft,
                state.plan,
                suspendedRecords[0].nodeId,
              );
              draft.status = "suspended";
            },
          );
          return;
        }
        throw new CoogoExecutionError(
          "No executable orchestration nodes remain",
          "dag_stalled",
        );
      }

      const expiredGate = ready.find((node) => {
        const gate = humanGateForNode(plan, node);
        return (
          gate !== undefined &&
          wasHumanGateSuspended(snapshot, node.id) &&
          this.now() >= Date.parse(gate.expiresAt)
        );
      });
      if (expiredGate) {
        const error = humanGateExpired(expiredGate.id);
        await this.cancelPendingNodes(state, error.message, error, true);
        await this.emit(
          state,
          "plan.cancelled",
          { reason: error.message, code: error.code },
          undefined,
          (draft) => {
            draft.status = "cancelled";
          },
        );
        return;
      }

      ready.sort((left, right) => order.get(left.id)! - order.get(right.id)!);
      for (const node of ready) {
        await this.emit(
          state,
          "node.started",
          { attempt: 1 },
          node.id,
          (draft) => {
            draft.nodes[node.id] = {
              nodeId: node.id,
              status: "running",
            };
          },
        );
      }

      const settledOutcomes = await Promise.all(
        ready.map((node) => this.runNode(state, node)),
      );
      const outcomes = settledOutcomes.map((outcome) =>
        controller.signal.aborted &&
        (outcome.status === "completed" || outcome.status === "suspended")
          ? abortedNodeOutcome(
              outcome.node,
              controller.signal,
              outcome.telemetry.attempts,
              outcome.telemetry.durationMs,
            )
          : outcome,
      );
      const suspendedOutcomes: NodeRunOutcome[] = [];
      for (const outcome of outcomes) {
        const eventTelemetry = checkpointTelemetry(outcome.telemetry);
        const record: CoogoNodeExecutionRecord = {
          nodeId: outcome.node.id,
          status: outcome.status,
          output: outcome.output,
          error: outcome.error,
          suspension: outcome.suspension,
          telemetry: outcome.telemetry,
        };
        const adoptRecord = (draft: CoogoExecutionSnapshot): void => {
          draft.nodes[outcome.node.id] = compactRecord(record);
        };
        if (outcome.status === "completed") {
          await this.emit(
            state,
            "node.completed",
            {
              status: outcome.status,
              ...eventTelemetry,
            },
            outcome.node.id,
            adoptRecord,
          );
        } else if (outcome.status === "suspended") {
          suspendedOutcomes.push(outcome);
        } else if (outcome.status === "failed") {
          await this.emit(
            state,
            "node.failed",
            {
              status: outcome.status,
              error: outcome.error,
              ...eventTelemetry,
            },
            outcome.node.id,
            adoptRecord,
          );
        } else {
          await this.emit(
            state,
            "node.cancelled",
            {
              status: outcome.status,
              error: outcome.error,
              ...eventTelemetry,
            },
            outcome.node.id,
            adoptRecord,
          );
        }
      }

      if (suspendedOutcomes.length > 0) {
        const hasFailure = scopedNodes.some(
          (node) => snapshot.nodes[node.id]?.status === "failed",
        );
        if (hasFailure) {
          for (const outcome of suspendedOutcomes) {
            const error = new CoogoExecutionError(
              `Node ${outcome.node.id} suspension was cancelled because a sibling node failed`,
              "sibling_failed",
            );
            await this.emit(
              state,
              "node.skipped",
              {
                status: "skipped",
                error: serializeError(error),
                ...checkpointTelemetry(outcome.telemetry),
              },
              outcome.node.id,
              (draft) => {
                draft.nodes[outcome.node.id] = {
                  nodeId: outcome.node.id,
                  status: "skipped",
                  error: serializeError(error),
                  telemetry: outcome.telemetry,
                };
              },
            );
          }
        } else {
          await this.emit(
            state,
            "plan.suspended",
            {
              nodeId: suspendedOutcomes[0].node.id,
              suspendedNodeIds: suspendedOutcomes.map(
                (outcome) => outcome.node.id,
              ),
              ...durableSuspensionEventData(state.plan, suspendedOutcomes[0]),
            },
            undefined,
            (draft) => {
              bindSnapshotAdmissionToNode(
                draft,
                state.plan,
                suspendedOutcomes[0].node.id,
              );
              for (const outcome of suspendedOutcomes) {
                draft.nodes[outcome.node.id] = compactRecord({
                  nodeId: outcome.node.id,
                  status: "suspended",
                  output: outcome.output,
                  suspension: outcome.suspension,
                  telemetry: outcome.telemetry,
                });
              }
              draft.status = "suspended";
            },
          );
          return;
        }
      }
    }

    if (
      controller.signal.aborted &&
      !isExecutionLeaseLoss(controller.signal.reason)
    ) {
      await this.cancelPendingNodes(state, active.reason, undefined, true);
      await this.emit(
        state,
        "plan.cancelled",
        { reason: active.reason ?? abortReason(controller.signal) },
        undefined,
        (draft) => {
          draft.status = "cancelled";
        },
      );
      return;
    }

    const hasFailure = scopedNodes.some(
      (node) => snapshot.nodes[node.id]?.status === "failed",
    );
    if (hasFailure) {
      await this.cancelPendingNodes(state, undefined, undefined, true);
      const records = scopedNodes.map((node) => snapshot.nodes[node.id]);
      await this.emit(
        state,
        "plan.failed",
        {
          failedNodeIds: records
            .filter((record) => record?.status === "failed")
            .map((record) => record.nodeId),
          skippedNodeIds: records
            .filter((record) => record?.status === "skipped")
            .map((record) => record.nodeId),
        },
        undefined,
        (draft) => {
          draft.status = "failed";
        },
      );
    } else {
      await this.emit(
        state,
        "plan.completed",
        { outputNodeIds: state.outputNodeIds },
        undefined,
        (draft) => {
          draft.status = "completed";
        },
      );
    }
  }

  private async runNode(
    state: ExecutionState,
    node: CoogoOrchestrationNode | CoogoOrchestrationNodeV2,
  ): Promise<NodeRunOutcome> {
    const startedAt = this.now();
    const humanGate = humanGateForNode(state.plan, node);
    if (humanGate) {
      const wasSuspended = wasHumanGateSuspended(state.snapshot, node.id);
      if (!wasSuspended) {
        return {
          node,
          status: "suspended",
          suspension: humanGateSuspension(node.id, humanGate),
          telemetry: { attempts: 1, durationMs: this.elapsed(startedAt) },
        };
      }
      if (this.now() >= Date.parse(humanGate.expiresAt)) {
        const error = humanGateExpired(node.id);
        if (!state.controller.signal.aborted) {
          state.controller.abort(error);
        }
        return {
          node,
          status: "cancelled",
          error: serializeError(error),
          telemetry: { attempts: 1, durationMs: this.elapsed(startedAt) },
        };
      }
      return {
        node,
        status: "completed",
        telemetry: { attempts: 1, durationMs: this.elapsed(startedAt) },
      };
    }
    const handler =
      resolveMockHandler(state.options.mocks?.[node.id]) ??
      this.registry.resolve(node);
    if (!handler) {
      return {
        node,
        status: "failed",
        error: serializeError(
          new CoogoExecutionError(
            `No handler registered for ${node.kind}${
              node.operation?.name ? `:${node.operation.name}` : ""
            }`,
            "handler_not_registered",
          ),
        ),
        telemetry: { attempts: 0, durationMs: this.elapsed(startedAt) },
      };
    }

    const dependencies = executionDependencies(state.plan, node);
    const unavailableDependency = dependencies.find(
      (dependency) =>
        state.snapshot.nodes[dependency.nodeId]?.error?.code ===
        "checkpoint_output_unavailable",
    );
    if (unavailableDependency) {
      return {
        node,
        status: "failed",
        error: serializeError(
          checkpointOutputUnavailable(unavailableDependency.nodeId),
        ),
        telemetry: { attempts: 0, durationMs: this.elapsed(startedAt) },
      };
    }
    const dependencyOutputs = Object.fromEntries(
      dependencies.map((dependency) => [
        dependency.nodeId,
        state.snapshot.nodes[dependency.nodeId]?.output,
      ]),
    );
    const canRetry =
      node.policy.retry === "safe" &&
      Boolean(node.policy.idempotencyKey?.trim());
    const attemptLimit = canRetry ? this.maxAttempts : 1;
    let attempts = 0;

    while (attempts < attemptLimit) {
      attempts += 1;
      try {
        await this.assertExecutionLease(state);
        const result = await this.invokeHandlerWithTimeout(
          handler,
          {
            plan: state.plan,
            node,
            signal: state.controller.signal,
            attempt: attempts,
            dependencyOutputs,
            resumeData: state.options.resumeData?.[node.id],
            executeSubplan: (subplan, options) =>
              this.execute(subplan, {
                ...options,
                signal: state.controller.signal,
              }),
          },
          node.policy.timeoutMs,
        );
        if (state.controller.signal.aborted) {
          return abortedNodeOutcome(
            node,
            state.controller.signal,
            attempts,
            this.elapsed(startedAt),
          );
        }
        const telemetry: CoogoNodeTelemetry = {
          ...(result.telemetry ?? {}),
          attempts,
          durationMs: this.elapsed(startedAt),
        };
        if (result.suspend) {
          return {
            node,
            status: "suspended",
            output: result.output,
            suspension: result.suspend,
            telemetry,
          };
        }
        return {
          node,
          status: "completed",
          output: result.output,
          telemetry,
        };
      } catch (error) {
        if (state.controller.signal.aborted) {
          return abortedNodeOutcome(
            node,
            state.controller.signal,
            attempts,
            this.elapsed(startedAt),
          );
        }
        if (attempts >= attemptLimit || !isRetryable(error)) {
          return {
            node,
            status: "failed",
            error: serializeError(error),
            telemetry: {
              attempts,
              durationMs: this.elapsed(startedAt),
            },
          };
        }
        if (this.retryDelayMs > 0) {
          try {
            await abortableDelay(this.retryDelayMs, state.controller.signal);
          } catch (delayError) {
            if (state.controller.signal.aborted) {
              return abortedNodeOutcome(
                node,
                state.controller.signal,
                attempts,
                this.elapsed(startedAt),
              );
            }
            return {
              node,
              status: "cancelled",
              error: serializeError(delayError),
              telemetry: {
                attempts,
                durationMs: this.elapsed(startedAt),
              },
            };
          }
        }
      }
    }

    throw new CoogoExecutionError("Unreachable retry state", "internal_error");
  }

  private async assertExecutionLease(state: ExecutionState): Promise<void> {
    if (!state.lease || !this.leaseController) return;
    try {
      if (state.controller.signal.aborted) {
        throw state.controller.signal.reason;
      }
      await this.leaseController.assertCurrent(
        state.lease,
        state.controller.signal,
      );
    } catch (cause) {
      const error = executionLeaseLost(cause);
      if (!state.controller.signal.aborted) state.controller.abort(error);
      throw error;
    }
  }

  private async renewExecutionLease(state: ExecutionState): Promise<void> {
    if (
      !state.lease ||
      !this.leaseController ||
      state.controller.signal.aborted
    ) {
      return;
    }
    try {
      const renewed = await this.leaseController.renew(
        state.lease,
        state.controller.signal,
      );
      assertLeaseHasHardExpiryMargin(renewed, this.now());
      state.lease = renewed;
    } catch (cause) {
      const error = executionLeaseLost(cause);
      if (!state.controller.signal.aborted) state.controller.abort(error);
    }
  }

  private async invokeHandlerWithTimeout(
    handler: CoogoNodeHandler,
    context: CoogoNodeHandlerContext,
    timeoutMs: number,
  ): Promise<CoogoNodeHandlerResult> {
    const attemptController = new AbortController();
    const detachAbort = forwardAbort(context.signal, attemptController);
    let timeout: ReturnType<typeof setTimeout> | undefined;
    const timeoutPromise = new Promise<never>((_resolve, reject) => {
      timeout = setTimeout(() => {
        const error = new CoogoExecutionTimeoutError(
          context.node.id,
          timeoutMs,
        );
        attemptController.abort(error);
        reject(error);
      }, timeoutMs);
    });
    const handlerPromise = Promise.resolve().then(() =>
      handler({ ...context, signal: attemptController.signal }),
    );

    try {
      return await Promise.race([handlerPromise, timeoutPromise]);
    } finally {
      if (timeout) clearTimeout(timeout);
      detachAbort();
    }
  }

  private async propagateUnsatisfiedDependencies(
    state: ExecutionState,
    nodes: Array<CoogoOrchestrationNode | CoogoOrchestrationNodeV2>,
  ): Promise<void> {
    let changed = true;
    while (changed) {
      changed = false;
      for (const node of nodes) {
        if (state.snapshot.nodes[node.id]?.status !== "pending") continue;
        const blockingDependencies = executionDependencies(
          state.plan,
          node,
        ).filter((dependency) =>
          dependencyCanNoLongerBeSatisfied(
            state.snapshot.nodes[dependency.nodeId]?.status,
            dependency.when,
          ),
        );
        if (blockingDependencies.length === 0) continue;
        const blockingNodeIds = blockingDependencies.map(
          (dependency) => dependency.nodeId,
        );
        const hasConditionalFailure = blockingDependencies.some(
          (dependency) => dependency.raw && typeof dependency.raw !== "string",
        );
        const error = new CoogoExecutionError(
          `Node ${node.id} skipped because dependency conditions cannot be met: ${blockingNodeIds.join(
            ", ",
          )}`,
          hasConditionalFailure
            ? "dependency_condition_not_met"
            : "dependency_failed",
        );
        await this.emit(
          state,
          "node.skipped",
          {
            status: "skipped",
            blockingDependencies: blockingNodeIds,
            error: serializeError(error),
            attempts: 0,
            durationMs: 0,
          },
          node.id,
          (draft) => {
            draft.nodes[node.id] = {
              nodeId: node.id,
              status: "skipped",
              error: serializeError(error),
              telemetry: { attempts: 0, durationMs: 0 },
            };
          },
        );
        changed = true;
      }
    }
  }

  private async cancelPendingNodes(
    state: ExecutionState,
    reason?: string,
    cancellationError?: CoogoExecutionError,
    includeSuspended = false,
  ): Promise<void> {
    for (const node of state.plan.nodes) {
      if (!state.scope.has(node.id)) continue;
      const record = state.snapshot.nodes[node.id];
      const status = record?.status;
      if (
        status !== "pending" &&
        status !== "running" &&
        !(includeSuspended && status === "suspended")
      ) {
        continue;
      }
      const error =
        cancellationError ?? new CoogoExecutionCancelledError(reason);
      await this.emit(
        state,
        "node.cancelled",
        {
          status: "cancelled",
          error: serializeError(error),
        },
        node.id,
        (draft) => {
          draft.nodes[node.id] = {
            nodeId: node.id,
            status: "cancelled",
            error: serializeError(error),
            telemetry:
              status === "suspended" && record?.telemetry
                ? record.telemetry
                : {
                    attempts: status === "pending" ? 0 : 1,
                    durationMs: 0,
                  },
          };
        },
      );
    }
  }

  private async emit(
    state: ExecutionState,
    type: CoogoExecutionEventV2["type"],
    data?: Record<string, unknown>,
    nodeId?: string,
    mutate?: (draft: CoogoExecutionSnapshot) => void,
  ): Promise<void> {
    const sequence = state.snapshot.sequence + 1;
    const event: CoogoExecutionEventV2 = {
      schemaVersion: COOGO_EXECUTION_EVENT_V2_VERSION,
      eventId: `${state.plan.runId}:${sequence}`,
      planId: state.plan.planId,
      runId: state.plan.runId,
      projectId: state.plan.projectId,
      sequence,
      occurredAt: new Date(this.now()).toISOString(),
      type,
      nodeId,
      data,
    };
    const nextSnapshot = cloneSnapshot(state.snapshot);
    mutate?.(nextSnapshot);
    nextSnapshot.sequence = sequence;
    nextSnapshot.events.push(event);
    nextSnapshot.updatedAt = event.occurredAt;
    const nextCheckpoint = checkpointFromSnapshot(nextSnapshot);
    let receipt: CoogoExecutionTransitionReceipt;
    try {
      receipt = await this.journal.commitTransition({
        expectedCheckpointRevision: state.checkpointRevision,
        event,
        nextCheckpoint,
        ...(state.lease
          ? {
              lease: {
                leaseId: state.lease.leaseId,
                fencingToken: state.lease.fencingToken,
              },
            }
          : {}),
      });
    } catch (cause) {
      if (state.lease && isLeaseAuthorityJournalError(cause)) {
        const error = executionLeaseLost(cause);
        if (!state.controller.signal.aborted) state.controller.abort(error);
        throw error;
      }
      if (state.lease && !state.controller.signal.aborted) {
        state.controller.abort(cause);
      }
      throw cause;
    }
    if (receipt.eventId !== event.eventId || receipt.sequence !== sequence) {
      const error = new CoogoExecutionError(
        "Journal acknowledged a different execution transition",
        "journal_receipt_mismatch",
      );
      if (state.lease && !state.controller.signal.aborted) {
        state.controller.abort(error);
      }
      throw error;
    }
    state.checkpointRevision = receipt.checkpointRevision;
    adoptSnapshot(state.snapshot, nextSnapshot);
    if (type === "plan.suspended") state.leaseDisposition = "suspended";
    if (
      type === "plan.completed" ||
      type === "plan.failed" ||
      type === "plan.cancelled"
    ) {
      state.leaseDisposition = "terminal";
    }
    await this.onEvent?.(event);
  }

  private elapsed(startedAt: number): number {
    return Math.max(0, this.now() - startedAt);
  }
}

function checkpointOutputUnavailable(nodeId: string): CoogoExecutionError {
  return new CoogoExecutionError(
    `Completed output for ${nodeId} is unavailable after checkpoint recovery`,
    "checkpoint_output_unavailable",
  );
}

function abortedNodeOutcome(
  node: CoogoOrchestrationNode | CoogoOrchestrationNodeV2,
  signal: AbortSignal,
  attempts: number,
  durationMs: number,
): NodeRunOutcome {
  const reason = signal.reason ?? new CoogoExecutionCancelledError();
  return {
    node,
    status: isExecutionLeaseLoss(reason) ? "failed" : "cancelled",
    error: serializeError(reason),
    telemetry: { attempts, durationMs },
  };
}

export function createCoogoMockHandler(
  definition:
    | CoogoNodeHandlerResult
    | ((
        context: CoogoNodeHandlerContext,
      ) => CoogoNodeHandlerResult | Promise<CoogoNodeHandlerResult>),
): CoogoNodeHandler {
  return typeof definition === "function" ? definition : async () => definition;
}

/**
 * Wraps a body in an explicitly bounded loop. The hard ceiling protects plans
 * even when an untrusted model requests an excessive iteration count.
 */
export function createCoogoLoopHandler(
  options: CoogoLoopHandlerOptions,
): CoogoNodeHandler {
  if (
    !Number.isInteger(options.maxIterations) ||
    options.maxIterations <= 0 ||
    options.maxIterations > COOGO_MAX_LOOP_ITERATIONS
  ) {
    throw new CoogoExecutionError(
      `maxIterations must be an integer between 1 and ${COOGO_MAX_LOOP_ITERATIONS}`,
      "invalid_loop_policy",
    );
  }

  return async (context) => {
    const outputs: unknown[] = [];
    let telemetry: CoogoHandlerTelemetry = {};
    let previousOutput: unknown;
    for (let iteration = 0; iteration < options.maxIterations; iteration += 1) {
      if (context.signal.aborted) {
        throw context.signal.reason ?? new CoogoExecutionCancelledError();
      }
      const loopContext: CoogoLoopBodyContext = {
        ...context,
        iteration,
        previousOutput,
      };
      const result = await options.body(loopContext);
      if (result.suspend) return result;
      outputs.push(result.output);
      previousOutput = result.output;
      telemetry = mergeTelemetry(telemetry, result.telemetry);
      const shouldContinue = options.shouldContinue
        ? await options.shouldContinue(result, loopContext)
        : result.continue !== false;
      if (!shouldContinue) break;
    }
    return {
      output: {
        iterations: outputs.length,
        outputs,
        finalOutput: previousOutput,
      },
      telemetry,
    };
  };
}

/** Creates a handler that delegates a node to a separately versioned subplan. */
export function createCoogoSubplanHandler(
  resolveSubplan: CoogoSubplanResolver,
): CoogoNodeHandler {
  return async (context) => {
    const resolved = await resolveSubplan(context);
    const plan = "plan" in resolved ? resolved.plan : resolved;
    const outputNodeIds =
      "plan" in resolved ? resolved.outputNodeIds : undefined;
    const resumeData =
      context.resumeData &&
      typeof context.resumeData === "object" &&
      !Array.isArray(context.resumeData)
        ? (context.resumeData as Record<string, unknown>)
        : undefined;
    const result = await context.executeSubplan(plan, {
      outputNodeIds,
      resumeData,
    });
    if (result.status === "suspended") {
      return {
        output: result.outputs,
        suspend: {
          reason: `Subplan ${plan.planId} suspended`,
          data: { runId: plan.runId },
        },
      };
    }
    if (result.status !== "completed") {
      throw new CoogoExecutionError(
        `Subplan ${plan.planId} ended with status ${result.status}`,
        "subplan_failed",
      );
    }
    return { output: result.outputs };
  };
}

function handlerKey(
  kind: CoogoOrchestrationNodeKind,
  operationName?: string,
): string {
  return operationName ? `${kind}:${operationName}` : kind;
}

function resolveMockHandler(
  definition: CoogoMockDefinition | undefined,
): CoogoNodeHandler | undefined {
  if (!definition) return undefined;
  return createCoogoMockHandler(definition);
}

function normalizeOutputNodeIds(
  plan: CoogoReadableOrchestrationPlan,
  outputNodeIds: string[] | undefined,
): string[] | null {
  if (!outputNodeIds) return null;
  const knownIds = new Set(plan.nodes.map((node) => node.id));
  const normalized = [...new Set(outputNodeIds)];
  if (normalized.length === 0) {
    throw new CoogoOrchestrationValidationError([
      "outputNodeIds must contain at least one node when provided",
    ]);
  }
  const unknown = normalized.filter((nodeId) => !knownIds.has(nodeId));
  if (unknown.length > 0) {
    throw new CoogoOrchestrationValidationError(
      unknown.map((nodeId) => `unknown output node: ${nodeId}`),
    );
  }
  return normalized;
}

function resolveExecutionScope(
  plan: CoogoReadableOrchestrationPlan,
  outputNodeIds: string[] | null,
): Set<string> {
  if (!outputNodeIds) return new Set(plan.nodes.map((node) => node.id));
  const nodes = new Map(plan.nodes.map((node) => [node.id, node]));
  const scope = new Set<string>();
  const include = (nodeId: string): void => {
    if (scope.has(nodeId)) return;
    scope.add(nodeId);
    const node = nodes.get(nodeId);
    if (!node) return;
    for (const dependency of executionDependencies(plan, node)) {
      include(dependency.nodeId);
    }
  };
  for (const nodeId of outputNodeIds) include(nodeId);
  let changed = true;
  while (changed) {
    changed = false;
    for (const node of plan.nodes) {
      if (scope.has(node.id)) continue;
      const protectsSelectedNode = executionDependencies(plan, node).some(
        (dependency) =>
          dependency.when === "failed" && scope.has(dependency.nodeId),
      );
      if (!protectsSelectedNode) continue;
      include(node.id);
      changed = true;
    }
  }
  return scope;
}

interface ExecutionDependency {
  nodeId: string;
  when: "completed" | "failed";
  raw: CoogoOrchestrationDependency;
}

function executionDependencies(
  plan: CoogoReadableOrchestrationPlan,
  node: CoogoOrchestrationNode | CoogoOrchestrationNodeV2,
): ExecutionDependency[] {
  if (plan.schemaVersion === "coogo.orchestration-plan.v1") {
    return ((node as CoogoOrchestrationNode).dependsOn ?? []).map((nodeId) => ({
      nodeId,
      when: "completed" as const,
      raw: nodeId,
    }));
  }
  const rawDependencies = (node as CoogoOrchestrationNodeV2).dependsOn ?? [];
  const normalized = normalizedDependencies(node as CoogoOrchestrationNodeV2);
  return normalized.map((dependency, index) => ({
    ...dependency,
    raw: rawDependencies[index]!,
  }));
}

function dependencyIsSatisfied(
  status: CoogoNodeExecutionStatus | undefined,
  when: "completed" | "failed",
): boolean {
  return status === when;
}

function dependencyCanNoLongerBeSatisfied(
  status: CoogoNodeExecutionStatus | undefined,
  when: "completed" | "failed",
): boolean {
  if (
    !status ||
    status === "pending" ||
    status === "running" ||
    status === "suspended"
  ) {
    return false;
  }
  if (status === "skipped" || status === "cancelled") return true;
  return status !== when;
}

function createSnapshot(
  plan: CoogoReadableOrchestrationPlan,
  planFingerprint: string,
  outputNodeIds: string[] | null,
  scope: Set<string>,
  now: () => number,
): CoogoExecutionSnapshot {
  const admission = humanGateAdmissionForPlan(plan);
  return {
    schemaVersion: COOGO_EXECUTION_SNAPSHOT_VERSION,
    planId: plan.planId,
    runId: plan.runId,
    projectId: plan.projectId,
    planFingerprint,
    outputNodeIds,
    ...(admission
      ? {
          admissionBinding: checkpointAdmissionBinding(
            admission.nodeId,
            admission.gate,
          ),
        }
      : {}),
    status: "running",
    sequence: -1,
    nodes: Object.fromEntries(
      plan.nodes
        .filter((node) => scope.has(node.id))
        .map((node) => [
          node.id,
          {
            nodeId: node.id,
            status: "pending",
          } satisfies CoogoNodeExecutionRecord,
        ]),
    ),
    events: [],
    updatedAt: new Date(now()).toISOString(),
  };
}

function checkpointFromSnapshot(
  snapshot: CoogoExecutionSnapshot,
): CoogoExecutionCheckpointV1 {
  return parseCoogoExecutionCheckpointV1({
    schemaVersion: COOGO_EXECUTION_CHECKPOINT_V1_VERSION,
    planId: snapshot.planId,
    runId: snapshot.runId,
    projectId: snapshot.projectId,
    planFingerprint: snapshot.planFingerprint,
    outputNodeIds: snapshot.outputNodeIds,
    ...(snapshot.admissionBinding
      ? { admissionBinding: structuredClone(snapshot.admissionBinding) }
      : {}),
    status: snapshot.status,
    sequence: snapshot.sequence,
    nodes: Object.fromEntries(
      Object.entries(snapshot.nodes).map(([nodeId, record]) => {
        const checkpointNode: CoogoExecutionCheckpointNodeV1 = {
          nodeId,
          status: record.status,
          ...(record.error ? { error: structuredClone(record.error) } : {}),
          ...(record.telemetry
            ? { telemetry: checkpointTelemetry(record.telemetry) }
            : {}),
        };
        if (
          COMPENSATION_NODE_IDS.has(nodeId) &&
          isCheckpointCompensationResult(record.output)
        ) {
          checkpointNode.checkpointOutput = structuredClone(record.output);
        }
        if (
          EXACT_IMPACT_NODE_IDS.has(nodeId) &&
          isCheckpointExactImpactCandidate(record.output)
        ) {
          checkpointNode.checkpointOutput = structuredClone(record.output);
        }
        if (
          record.status === "suspended" &&
          snapshot.admissionBinding?.gateNodeId === nodeId
        ) {
          const binding = snapshot.admissionBinding;
          checkpointNode.suspension = {
            kind: "human-gate",
            reason: record.suspension?.reason ?? "human_confirmation_required",
            gateNodeId: binding.gateNodeId,
            confirmationId: binding.confirmationId,
            expiresAt: binding.expiresAt,
            target: structuredClone(binding.target),
          };
        }
        return [nodeId, checkpointNode];
      }),
    ),
    updatedAt: snapshot.updatedAt,
  });
}

function checkpointTelemetry(
  telemetry: CoogoNodeTelemetry,
): NonNullable<CoogoExecutionCheckpointNodeV1["telemetry"]> {
  return Object.fromEntries(
    Object.entries(telemetry).filter(
      ([key, value]) =>
        [
          "attempts",
          "durationMs",
          "cost",
          "inputTokens",
          "outputTokens",
          "totalTokens",
        ].includes(key) &&
        typeof value === "number" &&
        Number.isFinite(value) &&
        value >= 0,
    ),
  ) as unknown as NonNullable<CoogoExecutionCheckpointNodeV1["telemetry"]>;
}

function isCheckpointCompensationResult(
  value: unknown,
): value is CoogoCheckpointCompensationOutputV1 {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const output = value as Record<string, unknown>;
  return (
    output.kind === "compensation-result" &&
    (output.disposition === "already-restored" ||
      output.disposition === "owned-undo") &&
    output.verified === true
  );
}

function isCheckpointExactImpactCandidate(
  value: unknown,
): value is CoogoCheckpointExactImpactOutputV1 {
  return (
    Boolean(value) &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    (value as { kind?: unknown }).kind === "exact-impact"
  );
}

function snapshotFromCheckpoint(
  checkpoint: CoogoExecutionCheckpointV1,
  events: CoogoExecutionEventV2[],
): CoogoExecutionSnapshot {
  return {
    schemaVersion: COOGO_EXECUTION_SNAPSHOT_VERSION,
    planId: checkpoint.planId,
    runId: checkpoint.runId,
    projectId: checkpoint.projectId,
    planFingerprint: checkpoint.planFingerprint,
    outputNodeIds: structuredClone(checkpoint.outputNodeIds),
    ...(checkpoint.admissionBinding
      ? { admissionBinding: structuredClone(checkpoint.admissionBinding) }
      : {}),
    status: checkpoint.status,
    sequence: checkpoint.sequence,
    nodes: Object.fromEntries(
      Object.entries(checkpoint.nodes).map(([nodeId, node]) => [
        nodeId,
        compactRecord({
          nodeId,
          status: node.status,
          error: node.error
            ? structuredClone(node.error)
            : node.status === "completed" && !node.checkpointOutput
              ? serializeError(checkpointOutputUnavailable(nodeId))
              : undefined,
          suspension: node.suspension
            ? {
                reason: node.suspension.reason,
                data: {
                  ...structuredClone(node.suspension),
                  target: structuredClone(node.suspension.target),
                },
              }
            : undefined,
          telemetry: node.telemetry
            ? structuredClone(node.telemetry)
            : undefined,
          output: node.checkpointOutput
            ? structuredClone(node.checkpointOutput)
            : undefined,
        }),
      ]),
    ),
    events: structuredClone(events),
    updatedAt: checkpoint.updatedAt,
  };
}

function validateAndPrepareSnapshot(
  snapshot: CoogoExecutionSnapshot,
  plan: CoogoReadableOrchestrationPlan,
  fingerprint: string,
  outputNodeIds: string[] | null,
  resumeData: Record<string, unknown> | undefined,
  now: () => number,
): CoogoExecutionSnapshot {
  if (snapshot.schemaVersion !== COOGO_EXECUTION_SNAPSHOT_VERSION) {
    throw new CoogoExecutionError(
      `Unsupported execution snapshot version: ${snapshot.schemaVersion}`,
      "snapshot_version_mismatch",
    );
  }
  if (
    snapshot.planId !== plan.planId ||
    snapshot.runId !== plan.runId ||
    snapshot.projectId !== plan.projectId ||
    snapshot.planFingerprint !== fingerprint ||
    JSON.stringify(snapshot.outputNodeIds) !== JSON.stringify(outputNodeIds)
  ) {
    throw new CoogoExecutionError(
      `Journal snapshot for run ${plan.runId} does not match the submitted plan`,
      "snapshot_plan_mismatch",
    );
  }
  validateSnapshotAdmissionBinding(snapshot, plan);
  const prepared = cloneSnapshot(snapshot);
  for (const record of Object.values(prepared.nodes)) {
    if (record.status === "running") {
      prepared.nodes[record.nodeId] = {
        nodeId: record.nodeId,
        status: "pending",
      };
    } else if (
      record.status === "suspended" &&
      Object.prototype.hasOwnProperty.call(resumeData ?? {}, record.nodeId)
    ) {
      const node = plan.nodes.find(
        (candidate) => candidate.id === record.nodeId,
      );
      const gate = node ? humanGateForNode(plan, node) : undefined;
      if (gate) {
        validateHumanGateResume(
          record.nodeId,
          gate,
          resumeData?.[record.nodeId],
          now(),
        );
      }
      prepared.nodes[record.nodeId] = {
        nodeId: record.nodeId,
        status: "pending",
      };
      prepared.status = "running";
    }
  }
  prepared.updatedAt = new Date(now()).toISOString();
  return prepared;
}

function validateSnapshotAdmissionBinding(
  snapshot: CoogoExecutionSnapshot,
  plan: CoogoReadableOrchestrationPlan,
): void {
  if (plan.schemaVersion !== "coogo.orchestration-plan.v2") return;
  const binding = snapshot.admissionBinding;
  const node = binding
    ? plan.nodes.find((candidate) => candidate.id === binding.gateNodeId)
    : undefined;
  const gate = node ? humanGateForNode(plan, node) : undefined;
  const expected = gate
    ? checkpointAdmissionBinding(node!.id, gate)
    : humanGateAdmissionForPlan(plan)
      ? null
      : undefined;
  if (
    expected === null ||
    stableStringify(binding) !== stableStringify(expected)
  ) {
    throw new CoogoExecutionError(
      `Journal snapshot for run ${plan.runId} has a contradictory human gate admission`,
      "snapshot_admission_mismatch",
    );
  }
}

function bindSnapshotAdmissionToNode(
  snapshot: CoogoExecutionSnapshot,
  plan: CoogoReadableOrchestrationPlan,
  nodeId: string,
): void {
  const node = plan.nodes.find((candidate) => candidate.id === nodeId);
  const gate = node ? humanGateForNode(plan, node) : undefined;
  // Generic tool-handler suspension remains supported and has no typed
  // admission authority to rotate. Only durable human gates own this binding.
  if (!gate) return;
  snapshot.admissionBinding = checkpointAdmissionBinding(nodeId, gate);
}

function validateHumanGateResumeAttempt(
  plan: CoogoReadableOrchestrationPlan,
  snapshot: CoogoExecutionSnapshot,
  resumeData: Record<string, unknown> | undefined,
  now: number,
): void {
  for (const node of plan.nodes) {
    const gate = humanGateForNode(plan, node);
    if (
      gate &&
      wasHumanGateSuspended(snapshot, node.id) &&
      Object.prototype.hasOwnProperty.call(resumeData ?? {}, node.id)
    ) {
      validateHumanGateResume(node.id, gate, resumeData?.[node.id], now);
    }
  }
}

function wasHumanGateSuspended(
  snapshot: CoogoExecutionSnapshot,
  nodeId: string,
): boolean {
  return snapshot.events.some((event) => {
    if (event.type !== "plan.suspended") return false;
    if (event.data?.nodeId === nodeId) return true;
    const suspendedNodeIds = event.data?.suspendedNodeIds;
    return Array.isArray(suspendedNodeIds) && suspendedNodeIds.includes(nodeId);
  });
}

function humanGateAdmissionForPlan(
  plan: CoogoReadableOrchestrationPlan,
): { nodeId: string; gate: CoogoHumanGateAdmission } | undefined {
  if (plan.schemaVersion !== "coogo.orchestration-plan.v2") return undefined;
  for (const node of plan.nodes) {
    const gate = humanGateForNode(plan, node);
    if (gate) return { nodeId: node.id, gate };
  }
  return undefined;
}

function humanGateForNode(
  plan: CoogoReadableOrchestrationPlan,
  node: CoogoOrchestrationNode | CoogoOrchestrationNodeV2,
): CoogoHumanGateAdmission | undefined {
  if (
    plan.schemaVersion !== "coogo.orchestration-plan.v2" ||
    node.kind !== "gate" ||
    node.policy.authority !== "human"
  ) {
    return undefined;
  }
  return (node as CoogoOrchestrationNodeV2).humanGate;
}

function checkpointAdmissionBinding(
  nodeId: string,
  gate: CoogoHumanGateAdmission,
): CoogoCheckpointAdmissionBindingV1 {
  return {
    gateNodeId: nodeId,
    confirmationId: gate.confirmationId,
    expiresAt: gate.expiresAt,
    principalBinding: "authenticated-owner",
    sessionBinding: "verified-jwt-session",
    target: structuredClone(gate.target),
    duplicatePolicy: "reject",
    durability: "durable",
  };
}

function humanGateSuspension(
  nodeId: string,
  gate: CoogoHumanGateAdmission,
): CoogoSuspension {
  const reason = "human_confirmation_required";
  return {
    reason,
    data: {
      kind: "human-gate",
      reason,
      gateNodeId: nodeId,
      confirmationId: gate.confirmationId,
      expiresAt: gate.expiresAt,
      target: structuredClone(gate.target),
    },
  };
}

function durableSuspensionEventData(
  plan: CoogoReadableOrchestrationPlan,
  outcome: NodeRunOutcome,
): CoogoSuspension {
  const suspension = outcome.suspension;
  if (humanGateForNode(plan, outcome.node) && suspension) {
    return structuredClone(suspension);
  }
  return { reason: suspension?.reason ?? "suspended" };
}

function validateHumanGateResume(
  nodeId: string,
  gate: CoogoHumanGateAdmission,
  value: unknown,
  now: number,
): void {
  if (now >= Date.parse(gate.expiresAt)) {
    throw humanGateExpired(nodeId);
  }
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw invalidHumanGateResume(nodeId);
  }
  const resume = value as Record<string, unknown>;
  const allowed = new Set(["confirmationId", "target", "confirmationPhrase"]);
  if (Object.keys(resume).some((key) => !allowed.has(key))) {
    throw invalidHumanGateResume(nodeId);
  }
  const target = resume.target;
  if (!target || typeof target !== "object" || Array.isArray(target)) {
    throw invalidHumanGateResume(nodeId);
  }
  const targetRecord = target as Record<string, unknown>;
  if (
    Object.keys(targetRecord).some(
      (key) => !new Set(["kind", "projectId", "pageId"]).has(key),
    ) ||
    resume.confirmationId !== gate.confirmationId ||
    resume.confirmationPhrase !== gate.confirmationPhrase ||
    targetRecord.kind !== gate.target.kind ||
    targetRecord.projectId !== gate.target.projectId ||
    targetRecord.pageId !== gate.target.pageId
  ) {
    throw invalidHumanGateResume(nodeId);
  }
}

function humanGateExpired(nodeId: string): CoogoExecutionError {
  return new CoogoExecutionError(
    `Human gate ${nodeId} admission has expired`,
    "human_gate_expired",
  );
}

function invalidHumanGateResume(nodeId: string): CoogoExecutionError {
  return new CoogoExecutionError(
    `Human gate ${nodeId} resume data does not match its admission`,
    "human_gate_resume_invalid",
  );
}

function hasResumableNode(
  snapshot: CoogoExecutionSnapshot,
  resumeData: Record<string, unknown> | undefined,
): boolean {
  return Object.values(snapshot.nodes).some(
    (record) =>
      record.status === "suspended" &&
      Object.prototype.hasOwnProperty.call(resumeData ?? {}, record.nodeId),
  );
}

function hasResumeData(
  resumeData: Record<string, unknown> | undefined,
): boolean {
  return Boolean(resumeData && Object.keys(resumeData).length > 0);
}

function buildExecutionResult(
  snapshot: CoogoExecutionSnapshot,
  outputNodeIds: string[] | null,
): CoogoExecutionResult {
  const selectedIds = outputNodeIds ?? Object.keys(snapshot.nodes);
  if (snapshot.status === "completed") {
    const unavailable = selectedIds.find(
      (nodeId) =>
        snapshot.nodes[nodeId]?.error?.code === "checkpoint_output_unavailable",
    );
    if (unavailable) throw checkpointOutputUnavailable(unavailable);
  }
  const outputs = Object.fromEntries(
    selectedIds.flatMap((nodeId) => {
      const record = snapshot.nodes[nodeId];
      return record?.status === "completed" &&
        record.error?.code !== "checkpoint_output_unavailable"
        ? [[nodeId, record.output]]
        : [];
    }),
  );
  const suspended = Object.values(snapshot.nodes).find(
    (record) => record.status === "suspended" && record.suspension,
  );
  return {
    planId: snapshot.planId,
    runId: snapshot.runId,
    projectId: snapshot.projectId,
    status: snapshot.status,
    outputs,
    nodes: cloneSnapshot(snapshot).nodes,
    events: cloneSnapshot(snapshot).events,
    suspension: suspended?.suspension
      ? { nodeId: suspended.nodeId, detail: suspended.suspension }
      : undefined,
  };
}

function compactRecord(
  record: CoogoNodeExecutionRecord,
): CoogoNodeExecutionRecord {
  return Object.fromEntries(
    Object.entries(record).filter((entry) => entry[1] !== undefined),
  ) as unknown as CoogoNodeExecutionRecord;
}

function serializeError(error: unknown): CoogoSerializedExecutionError {
  if (error instanceof CoogoExecutionError) {
    return {
      name: error.name,
      message: error.message,
      code: error.code,
      retryable: error.retryable,
    };
  }
  if (error instanceof Error) {
    const candidate = error as Error & { code?: unknown; retryable?: unknown };
    return {
      name: error.name,
      message: error.message,
      code:
        typeof candidate.code === "string" ? candidate.code : "handler_failed",
      retryable: candidate.retryable === true,
    };
  }
  return {
    name: "Error",
    message: typeof error === "string" ? error : "Unknown execution error",
    code: "handler_failed",
    retryable: false,
  };
}

function isRetryable(error: unknown): boolean {
  if (error instanceof CoogoExecutionError) return error.retryable;
  return (error as { retryable?: unknown } | null)?.retryable === true;
}

function forwardAbort(
  source: AbortSignal | undefined,
  target: AbortController,
): () => void {
  if (!source) return () => undefined;
  const abort = () => target.abort(source.reason);
  if (source.aborted) abort();
  else source.addEventListener("abort", abort, { once: true });
  return () => source.removeEventListener("abort", abort);
}

function abortReason(signal: AbortSignal): string {
  if (signal.reason instanceof Error) return signal.reason.message;
  return typeof signal.reason === "string"
    ? signal.reason
    : "Execution cancelled";
}

function executionLeaseLost(cause: unknown): CoogoExecutionError {
  if (
    cause instanceof CoogoExecutionError &&
    cause.code === "execution_lease_lost"
  ) {
    return cause;
  }
  const detail = cause instanceof Error ? cause.message : String(cause);
  return new CoogoExecutionError(
    `Execution lease lost: ${detail}`,
    "execution_lease_lost",
  );
}

function isExecutionLeaseLoss(value: unknown): value is CoogoExecutionError {
  return (
    value instanceof CoogoExecutionError &&
    value.code === "execution_lease_lost"
  );
}

function isLeaseAuthorityJournalError(value: unknown): boolean {
  if (!(value instanceof CoogoExecutionError)) return false;
  return new Set([
    "lease_fence_stale",
    "lease_fence_conflict",
    "journal_revision_conflict",
    "journal_sequence_conflict",
    "executor_checkpoint_revision_conflict",
  ]).has(value.code);
}

function adoptSnapshot(
  target: CoogoExecutionSnapshot,
  source: CoogoExecutionSnapshot,
): void {
  target.schemaVersion = source.schemaVersion;
  target.planId = source.planId;
  target.runId = source.runId;
  target.projectId = source.projectId;
  target.planFingerprint = source.planFingerprint;
  target.outputNodeIds = source.outputNodeIds;
  if (source.admissionBinding) {
    target.admissionBinding = source.admissionBinding;
  } else {
    delete target.admissionBinding;
  }
  target.status = source.status;
  target.sequence = source.sequence;
  target.nodes = source.nodes;
  target.events = source.events;
  target.updatedAt = source.updatedAt;
}

function assertLeaseHasHardExpiryMargin(
  lease: CoogoExecutionLease,
  now: number,
): void {
  const expiresAt = Date.parse(lease.expiresAt);
  if (!Number.isFinite(expiresAt) || expiresAt - now <= 5_000) {
    throw new CoogoExecutionError(
      "Execution lease expires inside the required 5-second safety margin",
      "execution_lease_lost",
    );
  }
}

function abortableDelay(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(signal.reason ?? new CoogoExecutionCancelledError());
      return;
    }
    const timeout = setTimeout(() => {
      signal.removeEventListener("abort", abort);
      resolve();
    }, ms);
    const abort = () => {
      clearTimeout(timeout);
      signal.removeEventListener("abort", abort);
      reject(signal.reason ?? new CoogoExecutionCancelledError());
    };
    signal.addEventListener("abort", abort, { once: true });
  });
}

function mergeTelemetry(
  current: CoogoHandlerTelemetry,
  next: CoogoHandlerTelemetry | undefined,
): CoogoHandlerTelemetry {
  if (!next) return current;
  const merged: CoogoHandlerTelemetry = { ...current, ...next };
  for (const key of [
    "cost",
    "inputTokens",
    "outputTokens",
    "totalTokens",
  ] as const) {
    if (typeof current[key] === "number" || typeof next[key] === "number") {
      merged[key] =
        (typeof current[key] === "number" ? current[key] : 0) +
        (typeof next[key] === "number" ? next[key] : 0);
    }
  }
  return merged;
}

function fingerprintPlan(
  plan: CoogoReadableOrchestrationPlan,
  outputNodeIds: string[] | null,
): string {
  const source = stableStringify({ plan, outputNodeIds });
  let hash = 0x811c9dc5;
  for (let index = 0; index < source.length; index += 1) {
    hash ^= source.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return `fnv1a-${(hash >>> 0).toString(16).padStart(8, "0")}`;
}

function stableStringify(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value.map((item) => stableStringify(item)).join(",")}]`;
  }
  if (value && typeof value === "object") {
    const object = value as Record<string, unknown>;
    return `{${Object.keys(object)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${stableStringify(object[key])}`)
      .join(",")}}`;
  }
  return JSON.stringify(value);
}

function cloneSnapshot(
  snapshot: CoogoExecutionSnapshot,
): CoogoExecutionSnapshot {
  if (typeof structuredClone === "function") return structuredClone(snapshot);
  return JSON.parse(JSON.stringify(snapshot)) as CoogoExecutionSnapshot;
}
